import React, { useState } from 'react';
import { ExternalLink, Github, ArrowRight, TrendingUp, FileText, MapPin, BarChart3, Package, Users, Zap } from 'lucide-react';
import { projectLinks } from '../data/projectLinks';
import { trackEvent } from '../utils/analytics';

interface Project {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  tags: string[];
  demo?: string;
  github?: string;
}

const Projects: React.FC = () => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const projects: Project[] = [
    {
      id: 1,
      title: 'Autonomous AI Trading System',
      description: 'Designed and implemented an AI-driven autonomous trading system using multi-agent architecture for market data analysis, sentiment tracking, strategy generation, and automated execution. Integrated real-time APIs such as YFinance, Finnhub.',
      icon: <TrendingUp className="h-12 w-12" />,
      gradient: 'from-green-500 via-emerald-500 to-teal-500',
      tags: ['Python', 'Machine Learning', 'NumPy', 'Pandas', 'APIs', 'Data Analysis'],
      demo: projectLinks.autonomousTrading.demo,
      github: projectLinks.autonomousTrading.github,
    },
    {
      id: 2,
      title: 'AI Interviewer System',
      description: 'Developed a resume parsing and skill evaluation system using Generative AI and PDF parsing techniques to extract user information and generate personalized interview feedback. Collaborated with a 4-member team for real-time performance feedback.',
      icon: <FileText className="h-12 w-12" />,
      gradient: 'from-blue-500 via-indigo-500 to-purple-500',
      tags: ['Python', 'Machine Learning', 'TensorFlow', 'Data Preprocessing', 'NLP'],
      demo: projectLinks.aiInterviewer.demo,
      github: projectLinks.aiInterviewer.github,
    },
    {
      id: 3,
      title: 'Personalized AI Roadmap Generator',
      description: 'Built an AI-powered personalized learning and career roadmap generator leveraging Generative AI for skill assessment and dynamic course recommendations. Designed user flows for goal-based learning paths to enhance career readiness.',
      icon: <MapPin className="h-12 w-12" />,
      gradient: 'from-orange-500 via-red-500 to-pink-500',
      tags: ['Python', 'Machine Learning', 'Scikit-learn', 'Data Analysis', 'MongoDB'],
      demo: projectLinks.personalizedRoadmap.demo,
      github: projectLinks.personalizedRoadmap.github,
    },
    {
      id: 4,
      title: 'DataCraft AI Studio - No-Code Data Science Platform',
      description: 'Developed a data science web application using Builder (no-code AI tool) for the frontend and custom backend logic for data processing, analysis, and visualization to gain actionable business insights.',
      icon: <BarChart3 className="h-12 w-12" />,
      gradient: 'from-cyan-500 via-blue-500 to-indigo-500',
      tags: ['Builder', 'Python', 'Data Preprocessing', 'NumPy', 'Pandas', 'Data Visualization'],
      demo: projectLinks.dataCraftAI.demo,
      github: projectLinks.dataCraftAI.github,
    },
    {
      id: 5,
      title: 'Personal & Family Finance Management App',
      description: 'Built a finance management web application using no-code AI tools (Lovable, Builder), with Supabase for authentication and database management. Designed business logic for automated expense tracking and financial planning.',
      icon: <Zap className="h-12 w-12" />,
      gradient: 'from-yellow-500 via-orange-500 to-red-500',
      tags: ['Lovable', 'Builder', 'Supabase', 'Vercel', 'JavaScript'],
      demo: projectLinks.financeManagement.demo,
      github: projectLinks.financeManagement.github,
    },
    {
      id: 6,
      title: 'Industrial Data Management System',
      description: 'Business data entry app with daily data tracking, automatic formulas, and visual analytics. Integrated Supabase for data storage and login with editing/viewing capabilities after submission.',
      icon: <BarChart3 className="h-12 w-12" />,
      gradient: 'from-slate-500 via-gray-500 to-zinc-500',
      tags: ['Supabase', 'JavaScript', 'HTML', 'CSS', 'SQL', 'Analytics'],
      demo: projectLinks.industrialData.demo,
      github: projectLinks.industrialData.github,
    },
    {
      id: 7,
      title: 'Inventory Management System',
      description: 'Built a responsive inventory management app using Builder.io with clean UI/UX. Integrated Supabase for authentication, real-time product tracking, and data storage with role-based access control.',
      icon: <Package className="h-12 w-12" />,
      gradient: 'from-violet-500 via-purple-500 to-fuchsia-500',
      tags: ['Builder', 'Supabase', 'JavaScript', 'HTML', 'CSS', 'SQL'],
      demo: projectLinks.inventoryManagement.demo,
      github: projectLinks.inventoryManagement.github,
    },
    {
      id: 8,
      title: '24-Hour Hackathon - Digital Youth Connect',
      description: 'Built a responsive frontend in 24 hours for "Hack the Web" Hackathon by KK Computers. Designed clean UI for courses, events, and role-based paths with smooth animations and reusable components.',
      icon: <Users className="h-12 w-12" />,
      gradient: 'from-lime-500 via-green-500 to-emerald-500',
      tags: ['HTML', 'CSS', 'JavaScript', 'Responsive Design', 'Hackathon'],
      demo: projectLinks.digitalYouthConnect.demo,
      github: projectLinks.digitalYouthConnect.github,
    },
    {
      id: 9,
      title: 'Visual Roadmap Tracker - Learning Platform',
      description: 'Built a dynamic roadmap builder with drag-and-drop functionality. Engineered a full-stack solution with MongoDB Atlas database, featuring real-time analytics dashboards and export options (PNG, PDF, JSON).',
      icon: <MapPin className="h-12 w-12" />,
      gradient: 'from-rose-500 via-pink-500 to-fuchsia-500',
      tags: ['MongoDB', 'JavaScript', 'HTML', 'CSS', 'Analytics', 'Export Options'],
      demo: projectLinks.visualRoadmap.demo,
      github: projectLinks.visualRoadmap.github,
    },
    {
      id: 10,
      title: 'TechFest - Event Management System',
      description: 'A modern, responsive event management system built with React, TypeScript, and Tailwind CSS. Features include event registration, admin dashboard, real-time analytics, 3D elements with Three.js, and complete frontend-only demo with mock authentication.',
      icon: <Users className="h-12 w-12" />,
      gradient: 'from-indigo-500 via-purple-500 to-pink-500',
      tags: ['React 18', 'TypeScript', 'Tailwind CSS', 'Three.js', 'Framer Motion', 'Chart.js'],
      demo: projectLinks.techFest.demo,
      github: projectLinks.techFest.github,
    },
  ];

  return (
    <section id="projects" className="py-24 bg-black relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-purple-950/5 to-black"></div>

      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'linear-gradient(0deg, transparent 24%, rgba(0, 179, 255, 0.3) 25%, rgba(0, 179, 255, 0.3) 26%, transparent 27%, transparent 74%, rgba(0, 179, 255, 0.3) 75%, rgba(0, 179, 255, 0.3) 76%, transparent 77%, transparent), linear-gradient(90deg, transparent 24%, rgba(0, 179, 255, 0.3) 25%, rgba(0, 179, 255, 0.3) 26%, transparent 27%, transparent 74%, rgba(0, 179, 255, 0.3) 75%, rgba(0, 179, 255, 0.3) 76%, transparent 77%, transparent)',
          backgroundSize: '50px 50px',
        }}
      ></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold font-orbitron mb-4 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-500">
            Featured Projects
          </h2>
          <div className="h-1 w-20 bg-gradient-to-r from-purple-500 to-blue-500 mx-auto rounded-full mb-8"></div>
          <p className="max-w-3xl mx-auto text-lg text-gray-300">
            AI/ML and software engineering projects showcasing expertise in Generative AI, Agentic systems, and intelligent automation solutions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className="relative overflow-hidden rounded-xl transition-all duration-500 transform hover:-translate-y-2"
              onMouseEnter={() => setHoverIndex(index)}
              onMouseLeave={() => setHoverIndex(null)}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br from-purple-600/20 to-blue-600/20 opacity-0 transition-opacity duration-500 ${hoverIndex === index ? 'opacity-100' : ''
                  }`}
              ></div>

              <div className="relative bg-gray-900/80 backdrop-blur-md border border-purple-900/30 rounded-xl flex flex-col min-h-full h-full overflow-hidden">
                <div className="relative h-48 overflow-hidden">
                  {/* Custom gradient background with icon */}
                  <div className={`w-full h-full bg-gradient-to-br ${project.gradient} opacity-20 transition-all duration-700`}
                    style={{
                      transform: hoverIndex === index ? 'scale(1.05)' : 'scale(1)',
                      backgroundSize: hoverIndex === index ? '200% 200%' : '100% 100%',
                      animation: hoverIndex === index ? 'gradient-shift 3s ease infinite' : 'none'
                    }}>
                  </div>

                  {/* Animated geometric pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0"
                      style={{
                        backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), 
                                           radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
                        backgroundSize: '100px 100px',
                        animation: hoverIndex === index ? 'float 3s ease-in-out infinite' : 'none'
                      }}>
                    </div>
                  </div>

                  {/* Central icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`relative text-white transition-all duration-500 ${hoverIndex === index ? 'scale-110 rotate-3' : 'scale-100'}`}>
                      {/* Icon glow effect */}
                      <div className={`absolute inset-0 bg-gradient-to-r ${project.gradient} rounded-full blur-xl opacity-30 transition-opacity duration-500 ${hoverIndex === index ? 'opacity-60' : 'opacity-30'}`}></div>
                      <div className="relative z-10">
                        {project.icon}
                      </div>
                    </div>
                  </div>

                  {/* Corner tech indicators */}
                  <div className="absolute top-4 right-4 opacity-60">
                    <div className="flex space-x-1">
                      {project.tags.slice(0, 2).map((_, tagIndex) => (
                        <div key={tagIndex} className="w-2 h-2 rounded-full bg-white/40"></div>
                      ))}
                    </div>
                  </div>

                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-xl font-orbitron font-bold mb-3 text-white">{project.title}</h3>
                  <p className="text-gray-300 mb-4 flex-grow">{project.description}</p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.tags.map((tag, tagIndex) => (
                      <span
                        key={tagIndex}
                        className="text-xs px-2 py-1 rounded-full bg-purple-900/30 text-purple-300 border border-purple-800/30"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex justify-between items-center mt-auto">
                    <div className="flex space-x-3">
                      {project.demo && project.demo !== '#' && (
                        <a
                          href={project.demo}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent.projectDemo(project.title)}
                          title="View Live Demo"
                        >
                          <ExternalLink className="h-5 w-5" />
                        </a>
                      )}
                      {project.github && project.github !== '#' && (
                        <a
                          href={project.github}
                          className="text-purple-400 hover:text-purple-300 transition-colors"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackEvent.socialClick('github-project')}
                          title="View Source Code"
                        >
                          <Github className="h-5 w-5" />
                        </a>
                      )}
                    </div>
                    <a
                      href={project.demo && project.demo !== '#' ? project.demo : project.github}
                      className="group flex items-center text-sm font-orbitron text-purple-400 hover:text-purple-300 transition-colors"
                      onClick={() => trackEvent.projectView(project.title)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {project.demo && project.demo !== '#' ? 'View Project' : 'View Code'}
                      <ArrowRight className="ml-1 h-4 w-4 transform transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;